import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { useSocket } from 'src/providers/socket.provider'
import { ProjectService } from 'src/services/project.service'
import { TBatchImportLogResult } from './batch-import.type'

const IDLE_REFRESH_INTERVAL_MS = 5000

type TParams = {
  projectId: number
  batchImportToken?: string
  interopLogIds?: number[]
}

export const useBatchImportStatus = ({ projectId, batchImportToken, interopLogIds }: TParams) => {
  const { socket } = useSocket()
  const [results, setResults] = useState<Record<number, TBatchImportLogResult>>({})
  // 403: tampered/old-format token, missing PATIENT:GET, or another project. Retrying can't help.
  const [isForbidden, setIsForbidden] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const hasExpectedIds = !!interopLogIds
  const expectedCount = interopLogIds?.length ?? 0
  const processedCount = Object.keys(results).length
  const isComplete =
    !!interopLogIds && interopLogIds.every((interopLogId) => results[interopLogId] !== undefined)

  const mergeResults = useCallback((incoming: TBatchImportLogResult[]) => {
    setResults((previous) => {
      const next = { ...previous }
      incoming.forEach((result) => {
        next[result.interopLogId] = result
      })
      return next
    })
  }, [])

  const fetchStatus = useCallback(async () => {
    if (!batchImportToken) return
    try {
      const { logs } = await ProjectService.getBatchImportStatus(projectId, batchImportToken)
      mergeResults(Object.values(logs))
      setHasFetched(true)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) setIsForbidden(true)
      console.warn('Failed to fetch batch import status', error)
    }
  }, [projectId, batchImportToken, mergeResults])

  // Socket: (re)subscribes on every (re)connection. The snapshot is fetched once the server acknowledges the
  // subscription, so every log is either in the snapshot or delivered live (the two ranges overlap).
  useEffect(() => {
    if (!socket || !batchImportToken || isComplete || isForbidden) return undefined

    const onLogProcessed = (result: TBatchImportLogResult) => mergeResults([result])

    socket.on('BATCH_IMPORT_LOG_PROCESSED', onLogProcessed)
    socket.emit('SUBSCRIBE_BATCH_IMPORT', { batchImportToken }, () => fetchStatus())

    return () => {
      socket.emit('UNSUBSCRIBE_BATCH_IMPORT', { batchImportToken })
      socket.off('BATCH_IMPORT_LOG_PROCESSED', onLogProcessed)
    }
  }, [socket, batchImportToken, isComplete, isForbidden, mergeResults, fetchStatus])

  // Single fetch when the expected ids are unknown (batch reopened from the URL after the ids were lost)
  useEffect(() => {
    if (batchImportToken && !hasExpectedIds) fetchStatus()
  }, [batchImportToken, hasExpectedIds, fetchStatus])

  // Initial snapshot (also covers a rejected subscription, which gets no ack)
  useEffect(() => {
    if (batchImportToken && hasExpectedIds) fetchStatus()
  }, [batchImportToken, hasExpectedIds, fetchStatus])

  // Auto-refresh while incomplete: the timer restarts whenever a new result lands, so it only fires
  // after 5s without any change (covers a dropped event, socket or not)
  useEffect(() => {
    if (!batchImportToken || !hasExpectedIds || isComplete || isForbidden) return undefined

    const interval = setInterval(fetchStatus, IDLE_REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [batchImportToken, hasExpectedIds, isComplete, isForbidden, processedCount, fetchStatus])

  // Reopened without the expected ids and still empty after a fetch: the 10-day retention has most likely passed
  const isExpired = hasFetched && !hasExpectedIds && processedCount === 0

  return { results, expectedCount, processedCount, isComplete, isForbidden, isExpired, refresh: fetchStatus }
}
